import { getCard, type GroupId } from '@lafayette/content';
import type { GameProjection, LegalCommand, OpponentView } from '@lafayette/protocol';

export function legalOfType<T extends LegalCommand['type']>(
  projection: GameProjection,
  type: T,
): Extract<LegalCommand, { type: T }>[] {
  return projection.legalCommands.filter(
    (c): c is Extract<LegalCommand, { type: T }> => c.type === type,
  );
}

export function titleOf(projection: GameProjection, instanceId: string): string {
  const definitionId = projection.cards[instanceId];
  return definitionId ? getCard(definitionId).title : 'a card';
}

export function valueOf(projection: GameProjection, instanceId: string): number {
  const definitionId = projection.cards[instanceId];
  return definitionId ? getCard(definitionId).value : 0;
}

export function nameOf(projection: GameProjection, playerId: string): string {
  if (playerId === projection.self.id) return 'you';
  return projection.opponents.find((o) => o.id === playerId)?.name ?? 'a player';
}

/** Clockwise turn order beginning after the viewer and ending with the viewer. */
export function everyone(projection: GameProjection): (OpponentView & { isSelf: boolean })[] {
  const all = [
    { ...projection.self, isSelf: true },
    ...projection.opponents.map((o) => ({ ...o, isSelf: false })),
  ];
  const playerById = new Map(all.map((player) => [player.id, player]));
  const seatOrder = projection.seatOrder.flatMap((id) => {
    const player = playerById.get(id);
    return player ? [player] : [];
  });
  const selfIndex = seatOrder.findIndex((player) => player.isSelf);

  return [...seatOrder.slice(selfIndex + 1), ...seatOrder.slice(0, selfIndex + 1)];
}

/** Which player currently holds a given property instance, if any. */
export function ownerOfInstance(
  projection: GameProjection,
  instanceId: string,
): { playerId: string; name: string; group: GroupId } | null {
  for (const player of everyone(projection)) {
    for (const [group, mall] of Object.entries(player.malls)) {
      if (mall?.cards.includes(instanceId)) {
        return { playerId: player.id, name: player.name, group: group as GroupId };
      }
    }
  }
  return null;
}

const STAGE_HEADLINES: Record<GameProjection['stage'], string> = {
  awaitingDraw: 'Draw to begin the turn',
  main: 'Play up to three cards',
  cancellation: 'Waiting on responses',
  payment: 'Debts are being settled',
  upgradePlacement: 'Placing a received upgrade',
  discard: 'Discarding down to the hand limit',
  finished: 'Match over',
};

export function stageHeadline(projection: GameProjection): string {
  if (projection.winnerId) {
    return projection.winnerId === projection.self.id
      ? 'You win!'
      : `${nameOf(projection, projection.winnerId)} wins`;
  }
  return STAGE_HEADLINES[projection.stage];
}

/** A short sentence naming exactly whose response the table is waiting on. */
export function waitingOn(projection: GameProjection): string | null {
  if (projection.winnerId) return null;

  if (projection.stage === 'cancellation' && projection.pendingAction) {
    const outstanding = projection.pendingAction.responders.filter(
      (id) => !projection.pendingAction!.responded.includes(id),
    );
    return `Waiting for ${outstanding.map((id) => nameOf(projection, id)).join(', ')} to respond to ${
      getCard(projection.pendingAction.definitionId).title
    }.`;
  }

  if (projection.stage === 'payment') {
    const debtors = [...new Set(projection.debts.map((d) => d.debtorId))];
    return `Waiting for ${debtors.map((id) => nameOf(projection, id)).join(', ')} to pay.`;
  }

  if (projection.stage === 'upgradePlacement') {
    return 'Waiting for a received upgrade to be placed.';
  }

  if (projection.stage === 'discard' && projection.discardRequired) {
    return `Waiting for ${nameOf(projection, projection.discardRequired.playerId)} to discard.`;
  }

  if (projection.turn.playerId !== projection.self.id) {
    return `${nameOf(projection, projection.turn.playerId)} is playing.`;
  }
  return null;
}

export const REJECTION_TEXT: Record<string, string> = {
  'not-your-turn': 'It is not your turn.',
  'wrong-phase': 'That is not available right now.',
  'version-conflict': 'The table moved on — try again.',
  'unknown-card': 'That card is not on the table.',
  'card-not-in-hand': 'That card is not in your hand.',
  'card-not-playable': 'That card cannot be played that way.',
  'illegal-target': 'That target is not legal.',
  'no-actions-remaining': 'You have used all three actions.',
  'must-draw-first': 'Draw first.',
  'match-finished': 'The match is over.',
  'invalid-payment': 'That payment is not legal.',
  'invalid-discard': 'Discard exactly the required number of cards.',
  'not-a-responder': 'You are not part of that response.',
  'already-responded': 'You already responded.',
  'no-such-debt': 'That debt is already settled.',
  'not-enough-players': 'Not enough players.',
};
